export const DDZ_INVITE_PATH = "/ddz/invite";
const INVITE_ATTRIBUTION_KEYS = ["aid", "f", "c", "p"];
const INVITER_RID = /^[1-9][0-9]{0,19}$/;
const RETURN_ORIGIN = "https://room-invite.invalid";

export function readInviteToken(value) {
  if (typeof value !== "string" || !/^ehi_[a-f0-9]{48}$/.test(value)) throw new Error("INVALID_INVITE");
  return value;
}

// Share URLs identify an invitation. Room details always come from the server.
export function inviteFromSearch(search) {
  const values = new URLSearchParams(search).getAll("invite_token");
  if (!values.length) return null;
  return values.length === 1 ? values[0] : "";
}

function readAttribution(searchOrAttribution) {
  const query = new URLSearchParams();
  if (typeof searchOrAttribution === "string" || searchOrAttribution instanceof URLSearchParams) {
    const incoming = new URLSearchParams(searchOrAttribution);
    INVITE_ATTRIBUTION_KEYS.forEach(key => incoming.getAll(key).forEach(value => query.append(key, value)));
  } else if (searchOrAttribution && typeof searchOrAttribution === "object") {
    INVITE_ATTRIBUTION_KEYS.forEach(key => {
      if (typeof searchOrAttribution[key] === "string") query.set(key, searchOrAttribution[key]);
    });
  }
  const attribution = {};
  INVITE_ATTRIBUTION_KEYS.forEach(key => {
    const values = query.getAll(key);
    if (values.length !== 1) return;
    const value = values[0];
    if (!value || value === "undefined" || value === "null" || value.length > 128 ||
        value.trim() !== value || /[\u0000-\u001f\u007f]/.test(value)) return;
    if (key === "aid" && !INVITER_RID.test(value)) return;
    attribution[key] = value;
  });
  return attribution;
}

// Preserve malformed invitation tokens so the existing preview flow can report the error.
export function readInviteContext(search) {
  const inviteToken = inviteFromSearch(search);
  return inviteToken === null ? null : { inviteToken, attribution: readAttribution(search) };
}

export function inviteLocation(inviteToken, searchOrAttribution = "") {
  const query = new URLSearchParams({ invite_token: inviteToken });
  const attribution = readAttribution(searchOrAttribution);
  INVITE_ATTRIBUTION_KEYS.forEach(key => { if (attribution[key]) query.set(key, attribution[key]); });
  return `${DDZ_INVITE_PATH}?${query.toString()}`;
}

function localInviteReturnUrl(backTo) {
  if (typeof backTo !== "string" || !backTo.startsWith("/") || backTo.startsWith("//")) return null;
  try {
    const url = new URL(backTo, RETURN_ORIGIN);
    if (url.origin !== RETURN_ORIGIN || url.username || url.password || url.href.includes("#") ||
        ![DDZ_INVITE_PATH, "/home"].includes(url.pathname)) return null;
    return url;
  } catch (_) { return null; }
}

export function inviteAuthReturnPath(backTo) {
  const url = localInviteReturnUrl(backTo);
  const context = url && readInviteContext(url.search);
  if (!context) return null;
  try { readInviteToken(context.inviteToken); } catch (_) { return null; }
  return inviteLocation(context.inviteToken, context.attribution);
}

export function inviteRegistrationAgentRid(backTo, storedAgentRid) {
  const url = localInviteReturnUrl(backTo);
  const context = url && readInviteContext(url.search);
  if (context) {
    try { readInviteToken(context.inviteToken); } catch (_) { return ""; }
    return context.attribution.aid || "";
  }
  // An invalid invitation target must not fall back to a different cached inviter.
  if (typeof backTo === "string") {
    try {
      if (new URL(backTo, RETURN_ORIGIN).searchParams.has("invite_token")) return "";
    } catch (_) { /* Ordinary non-invitation return targets keep existing attribution. */ }
  }
  return storedAgentRid || "";
}

// Add only the authenticated hall account's public referral identity to a server invitation.
export function buildShareInviteUrl(serverUrl, { rid, channel, platform } = {}, hallOrigin) {
  let origin, url;
  try {
    origin = new URL(hallOrigin);
    url = new URL(serverUrl, origin.origin);
  } catch (_) { throw new Error("INVALID_INVITE_URL"); }
  if (origin.protocol !== "https:" || origin.username || origin.password ||
      url.protocol !== "https:" || url.origin !== origin.origin || url.username || url.password || url.href.includes("#") ||
      ![DDZ_INVITE_PATH, "/home"].includes(url.pathname)) throw new Error("INVALID_INVITE_URL");
  const context = readInviteContext(url.search);
  try { readInviteToken(context && context.inviteToken); } catch (_) { throw new Error("INVALID_INVITE_URL"); }
  // Never stringify an unsafe numeric RID: it may already have lost integer precision.
  const agentRid = typeof rid === "string" ? rid : Number.isSafeInteger(rid) ? String(rid) : "";
  if (!INVITER_RID.test(agentRid)) throw new Error("INVALID_INVITER_RID");
  return new URL(inviteLocation(context.inviteToken, {
    aid: agentRid, f: "room", c: channel, p: platform,
  }), origin.origin).href;
}

export function readInvitePreview(info) {
  if (!info || info.code !== "OK" ||
      !Number.isSafeInteger(info.game_id) || info.game_id <= 0 ||
      !Number.isSafeInteger(info.room_code) || info.room_code <= 0 ||
      !Number.isSafeInteger(info.club_id) || info.club_id < 0 ||
      !Number.isSafeInteger(info.capacity) || info.capacity <= 0 ||
      !Number.isSafeInteger(info.seat_count) || info.seat_count < 0 || info.seat_count > info.capacity ||
      !Number.isSafeInteger(info.expires_at) || info.expires_at <= 0) throw new Error("INVALID_INVITE_RESPONSE");
  if (info.expires_at * 1000 <= Date.now()) throw new Error("INVITE_EXPIRED");
  return { game_id: info.game_id, room_code: info.room_code, club_id: info.club_id,
    capacity: info.capacity, seat_count: info.seat_count, expires_at: info.expires_at };
}
